import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import type { ImportBaseMapping, ImportDefaultChannel, ImportResult } from '../../lib/types';

export interface ImportCustomerBaseInput {
  rows: Record<string, string>[];
  mapping: ImportBaseMapping;
  defaultChannel: ImportDefaultChannel;
}

/**
 * useImportCustomerBase — Item 391-C.
 *
 * Mutation que envia a base parseada (`rows` + `mapping`) ao backend
 * (`POST /v1/customer-registry/import`). Em sucesso devolve o `ImportResult`
 * (a página renderiza o painel de resultado); em erro emite toast. Não invalida
 * caches: a importação é um ato de carga em massa cujo efeito (Identities/Bonds/
 * Pendencies) é assíncrono ao estado da UI corrente.
 *
 * Cap gate backend: `customer-registry-import` (403 → toast de erro).
 */
export function useImportCustomerBase(): {
  importBase: (input: ImportCustomerBaseInput) => Promise<ImportResult>;
  isPending: boolean;
  result: ImportResult | undefined;
  reset: () => void;
} {
  const mutation = useMutation<ImportResult, unknown, ImportCustomerBaseInput>({
    mutationFn: ({ rows, mapping, defaultChannel }) =>
      api.importCustomerBase(rows, mapping, defaultChannel),
    onSuccess: (data) => {
      useToastStore.getState().push({
        variant: 'success',
        title: 'Base importada',
        description:
          `${data.total} linha(s) processada(s) — ` +
          `${data.identitiesCreated} criada(s), ${data.identitiesPromoted} promovida(s).`,
        durationMs: 4500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Falha ao importar a base',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  return {
    importBase: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    result: mutation.data,
    reset: mutation.reset,
  };
}
